
import DeviceMap from "@/components/DeviceMap";
import { useLanguage } from "@/contexts/LanguageContext";
import { Location } from "../types";
import { locations } from "@/data/locations";
import { getMediaOwnerFromLocationName } from "@/components/budget/mediaOwnerConstants";

interface DeviceSectionProps {
  selectedLocations?: Location[];
  onDeviceSelect: (devices: number) => void;
  onLocationsChange: (locations: Location[]) => void;
}

const DeviceSection = ({ 
  selectedLocations = [],
  onDeviceSelect, 
  onLocationsChange 
}: DeviceSectionProps) => {
  const { t, language } = useLanguage();

  // Convert the selected locations to the format expected by DeviceMap
  const importedLocations = selectedLocations.map(loc => ({
    id: (loc as any).id,
    name: loc.name,
    devices: loc.devices || 0,
    mediaOwner: loc.mediaOwner,
    region: (loc as any).region
  }));

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border/40 p-4 sm:p-6">
      <div className={`flex flex-row items-center justify-between mb-4 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
        <h2 className={`text-lg sm:text-xl font-semibold text-foreground flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
          {t('locationSelection')}
        </h2>
      </div>
      
      <DeviceMap 
        onDeviceSelect={(deviceCount) => {
          localStorage.setItem('devices', deviceCount.toString());
          console.log('Device map directly set devices count:', deviceCount);
          onDeviceSelect(deviceCount);
        }}
        onLocationsChange={(selectedLocations) => {
          console.log("Raw selected locations:", selectedLocations);
          
          const enhancedLocations = selectedLocations.map(loc => {
            const normalize = (s: any) =>
              (s ?? '')
                .toString()
                .replace(/^\s*\d+\s*[-–—.).]\s*/, '') // strip leading indices like "1 - "
                .toLowerCase()
                .normalize('NFKD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/[^\p{L}\p{N}\s]/gu, '')
                .replace(/\bscreens\b/g, 'screen')
                .replace(/\s+/g, ' ')
                .trim();

            let fullLocationData: any = undefined;

            // Prefer ID if available
            if (typeof (loc as any).id === 'number') {
              fullLocationData = locations.find(l => l.id === (loc as any).id);
            }
            
            if (!fullLocationData) {
              const nameKey = normalize(loc.name);
              const regionKey = normalize((loc as any).region);
              const ownerKey = normalize((loc as any).mediaOwner);
 
              // Exact name equality only
              let candidates = locations.filter(l => normalize(l.name) === nameKey);
 
              // Disambiguate by region/owner when available
              if (candidates.length > 1 && regionKey) {
                candidates = candidates.filter(l => normalize(l.region) === regionKey);
              }
              if (candidates.length > 1 && ownerKey) {
                candidates = candidates.filter(l => normalize(l.mediaOwner) === ownerKey);
              }
 
              fullLocationData = candidates?.[0];
            }
            
            if (!fullLocationData) {
              console.warn(`Cannot find full data for location: ${loc.name}`);
            }
            
            let mediaOwner = fullLocationData?.mediaOwner;
            
            if (!mediaOwner) {
              mediaOwner = getMediaOwnerFromLocationName(loc.name);
              console.log(`Determined media owner for ${loc.name}: ${mediaOwner}`);
            }
            
            return {
              ...(loc as any),
              id: (loc as any).id ?? fullLocationData?.id,
              mediaOwner: mediaOwner
            } as any;
          });
          
          const totalDevices = enhancedLocations.reduce((sum, loc) => sum + (loc.devices || 0), 0);
          
          localStorage.setItem('totalDevices', totalDevices.toString());
          localStorage.setItem('devices', totalDevices.toString());
          console.log('Total devices from all locations:', totalDevices);
          
          console.log("Enhanced locations with media owners:", enhancedLocations);
          
          if (enhancedLocations.length > 0) {
            const mediaOwnerCounts = enhancedLocations.reduce((counts, loc) => {
              const mediaOwner = loc.mediaOwner || 'TableAds';
              counts[mediaOwner] = (counts[mediaOwner] || 0) + 1;
              return counts;
            }, {} as Record<string, number>);
            
            const entries = Object.entries(mediaOwnerCounts) as Array<[string, number]>;
            const dominantMediaOwner = entries.sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0))[0]?.[0] || 'TableAds';
            
            localStorage.setItem('dominantMediaOwner', dominantMediaOwner);
            console.log("Dominant media owner set to:", dominantMediaOwner);
          }
          
          onLocationsChange(enhancedLocations);
        }}
        importedLocations={importedLocations}
      />
    </div>
  );
};

export default DeviceSection;
